import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, User, Users, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card, { CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { trackEvent, trackException, setUserProperties } from '../../lib/analytics';

const ProfileComplete: React.FC = () => {
  const { user, updateUserProfile, userProfile, error: authError, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    gender: 'male',
    email: '',
    birthDate: '',
    birthTime: '',
    birthPlace: '',
    birthCountry: 'India',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [mounted, setMounted] = useState(false);

  // Mark component as mounted
  useEffect(() => {
    setMounted(true);
    trackEvent('profile_completion', 'visit', 'new_user');
  }, []);

  // Pre-fill form with existing profile data if available
  useEffect(() => {
    if (userProfile && mounted) {
      setFormData({
        name: userProfile.name || '',
        gender: userProfile.gender || 'male',
        email: userProfile.email || '',
        birthDate: userProfile.birth_date || '',
        birthTime: userProfile.birth_time || '',
        birthPlace: userProfile.birth_location?.city || '',
        birthCountry: userProfile.birth_location?.country || 'India',
      });
    }
  }, [userProfile, mounted]);

  // Check if profile is complete
  const isProfileComplete = () => {
    return userProfile && 
           userProfile.name && 
           userProfile.name.trim() !== '' &&
           userProfile.birth_date && 
           userProfile.birth_date.trim() !== '' &&
           userProfile.birth_time && 
           userProfile.birth_time.trim() !== '' &&
           userProfile.birth_location && 
           userProfile.birth_location.city && 
           userProfile.birth_location.city.trim() !== '';
  };

  // Redirect if profile is complete
  useEffect(() => {
    if (mounted && !authLoading && isProfileComplete()) {
      navigate('/', { replace: true });
    }
  }, [userProfile, mounted, authLoading, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    trackEvent('profile_form', 'field_edit', name);
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.birthDate) {
      newErrors.birthDate = 'Date of birth is required';
    }
    
    if (!formData.birthTime) {
      newErrors.birthTime = 'Birth time is required';
    }
    
    if (!formData.birthPlace.trim()) {
      newErrors.birthPlace = 'Birth place is required';
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      trackEvent('profile_form', 'validation_failed', Object.keys(newErrors).join(','));
    }
    
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    trackEvent('profile_completion', 'attempt', 'form_submit');
    
    try {
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      const profileData = {
        name: formData.name,
        gender: formData.gender,
        email: formData.email || undefined,
        birth_date: formData.birthDate,
        birth_time: formData.birthTime,
        birth_location: {
          city: formData.birthPlace,
          country: formData.birthCountry
        }
      };

      await updateUserProfile(profileData);
      
      // Set user properties in GA
      setUserProperties({
        user_gender: formData.gender,
        user_country: formData.birthCountry,
        profile_completed: true,
        has_email: !!formData.email
      });
      
      trackEvent('profile_completion', 'success', 'profile_created');
      trackEvent('user_onboarding', 'complete', 'profile_setup');
      
      navigate('/', { replace: true });
    } catch (error: any) {
      console.error('Error updating profile:', error);
      setErrors({
        form: 'Failed to update profile. Please try again.',
      });
      
      trackEvent('profile_completion', 'error', error.message || 'unknown_error');
      trackException(`Profile completion failed: ${error.message}`, false);
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while auth is loading
  if (authLoading || !mounted) {
    return (
      <div className="min-h-screen bg-cosmic-gradient flex flex-col items-center justify-center px-4 py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white">Loading your profile...</p>
        </div>
      </div>
    );
  }

  // For new users, don't show error if no user is set yet - they will be auto-logged in
  if (!user && !userProfile) {
    return (
      <div className="min-h-screen bg-cosmic-gradient flex flex-col items-center justify-center px-4 py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white">Setting up your account...</p>
        </div>
      </div>
    );
  }

  // Only show error if we're sure there's an authentication issue
  if (!user && mounted && !authLoading) {
    return (
      <div className="min-h-screen bg-cosmic-gradient flex flex-col items-center justify-center px-4 py-12">
        <Card className="max-w-md w-full border-red-200">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-900 mb-2">Authentication Error</h2>
            <p className="text-red-700 mb-4">Please log in again to continue.</p>
            <Button onClick={() => navigate('/auth')} fullWidth>
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cosmic-gradient flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white font-display">Complete Your Profile</h1>
          <p className="mt-2 text-secondary-300">We need your birth details for accurate predictions</p>
        </div>
        
        {/* Main Form */}
        <Card className="w-full">
          <CardHeader>
            <CardTitle>Birth Details</CardTitle>
          </CardHeader>
          <CardContent>
            {authError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                  <div>
                    <p className="text-sm text-red-800 font-medium">Authentication Error</p>
                    <p className="text-sm text-red-700">{authError}</p>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              <Input
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your full name"
                leftIcon={<User size={18} className="text-neutral-500" />}
                error={errors.name}
                fullWidth
                disabled={loading}
                required
              />
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Gender <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <Users size={18} className="text-neutral-500" />
                  </div>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className="block rounded-lg border px-4 py-2 pl-10 w-full text-neutral-900 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-shadow border-neutral-300"
                    disabled={loading}
                    required
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
              </div>
              
              <Input
                label="Email (Optional)"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                fullWidth
                disabled={loading}
              />
              
              <Input
                label="Date of Birth"
                name="birthDate"
                type="date"
                value={formData.birthDate}
                onChange={handleChange}
                leftIcon={<Calendar size={18} className="text-neutral-500" />}
                error={errors.birthDate}
                fullWidth
                disabled={loading}
                required
              />
              
              <Input
                label="Birth Time"
                name="birthTime"
                type="time"
                value={formData.birthTime}
                onChange={handleChange}
                leftIcon={<Clock size={18} className="text-neutral-500" />}
                helperText="As accurate as possible for better predictions"
                error={errors.birthTime}
                fullWidth
                disabled={loading}
                required
              />
              
              <Input
                label="Birth Place"
                name="birthPlace"
                value={formData.birthPlace}
                onChange={handleChange}
                placeholder="City name"
                leftIcon={<MapPin size={18} className="text-neutral-500" />}
                error={errors.birthPlace}
                fullWidth
                disabled={loading}
                required
              />
              
              <Input
                label="Country"
                name="birthCountry"
                value={formData.birthCountry}
                onChange={handleChange}
                placeholder="Country"
                fullWidth
                disabled={loading}
              />
              
              {errors.form && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                    <p className="text-sm text-red-800">{errors.form}</p>
                  </div>
                </div>
              )}
              
              <Button
                type="submit"
                isLoading={loading}
                className="mt-6"
                fullWidth
              >
                Complete Profile
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProfileComplete;